import { findCarriedInventoryEntry } from '../janus-engine/inventory/find-inventory-entry';
import { planAddCasterInventory, type AddCasterCommand } from '../janus-engine/inventory/add-caster';
import type { SpawnSourcePackage } from '../janus-engine/spawn/source-plan';
import { fromBaseRecord, fromBaseUuid } from '../janus-engine/compile/from-base-context';
import { surfaceField } from '../janus-engine/compile/from-base-encounter-media';
import { encounterCreatureField } from './array-types';
import { ftClockCalendarFromArr, ftClockCalendarFromRaw, ftClockCampaignField, ftClockBaseFromArr, ftDeriveCampaignClock, type CampaignCalendarEmbed } from './campaign-clock';
import { readInventoryField, readInventoryRows } from '../janus-engine/compile/inventory-raw-readers';
import { mergeInventoryMetadata } from '../janus-engine/compile/inventory-shared-catalog';
import { inventoryCreationSizeTemplateIds } from '../janus-engine/ability/timed-finish/inventory';
import { browserRollSource } from '../janus-engine/actions/roll-source';
import { refuseUnported } from '../janus-engine/unported';
import { SPOILING_STATUS_ID } from '../janus-engine/inventory/birth-inputs-20';
import type { BasePatchFrame } from '../janus-engine/compile/base-patch';
import type { AppliedSaveInput } from './applied-save-queue';
import { holdInventoryBirth } from './inventory-birth-persistence';
import type { AppliedFrame } from '../janus-engine/transport/applied-frame';
import { withAsyncDeadline } from './async-deadline';

type Row = Record<string, unknown>;
type Cache = { get(id: string): unknown; values?(): IterableIterator<unknown> };
type Page = {
    FateTide?: { casterCache?: Cache; objectCache?: Cache; campaignCache?: Cache; encounterCache?: Cache };
    ftRerenderPcSheet?: (id: string, changedPaths?: readonly string[]) => unknown;
    ftBaseHeardRevision?: (id: string) => number | undefined;
    ftSeedCasterFromEncounterCache?: (id: string) => boolean;
    ftResyncEncounter?: (id: string) => Promise<unknown>;
    ftPaintCasterInventoryPlan?: (command: AddCasterCommand, input: SpawnSourcePackage) => (() => void) & { applied_frame?: AppliedFrame };
};
interface AddTiming {
    casterId: string; operationId?: string; startedAt: number; stages: { name: string; ms: number }[];
    paintedMs?: number; paintError?: string; commitAnswerMs?: number; totalMs?: number; outcome: string;
}
const pending = new Map<string, { fingerprint: string; command: AddCasterCommand; input: SpawnSourcePackage }>();
const running = new Set<string>();
const calendars = new Map<string, Promise<CampaignCalendarEmbed>>();
let timingSequence = 0;

function calendarMonths(calendarId: string): Promise<CampaignCalendarEmbed> {
    let promise = calendars.get(calendarId);
    if (!promise) {
        promise = (async () => {
            const response = await fetch(BASE_URL + 'options/api/calendars_list_inc.php?item_type=calendar');
            const data = await response.json() as { client_catalog?: { base?: { calendar_months?: Row[] } } };
            if (!response.ok || !Array.isArray(data.client_catalog?.base?.calendar_months)) throw new Error('add-inventory:calendar-inputs-unavailable');
            const months = data.client_catalog.base.calendar_months.filter(row => surfaceField(row, 'item_id') === calendarId && !Number(surfaceField(row, 'calendar_month_deleted') ?? 0));
            return ftClockCalendarFromRaw({ months, weekdays: [], max_month_order: Math.max(0, ...months.map(row => Number(surfaceField(row, 'month_order') ?? 0))),
                sum_days_in_year: months.reduce((sum, row) => sum + Number(surfaceField(row, 'days_in_month') ?? 0), 0) });
        })();
        calendars.set(calendarId, promise);
        void promise.catch(() => { if (calendars.get(calendarId) === promise) calendars.delete(calendarId); });
    }
    return promise;
}

/** The page's existing receiver cache/compile owner stays in its original bundle. */
export async function addCasterInventory(casterId: string, values: Row, warningId: string): Promise<void> {
    const id = casterId.trim().toUpperCase();
    if (running.has(id)) return;
    running.add(id);
    const perf = typeof performance === 'undefined' ? null : performance;
    const now = (): number => perf?.now() ?? Date.now();
    const started = now(), prefix = `ft:add-inventory:${++timingSequence}`;
    const timing: AddTiming = { casterId: id, startedAt: Date.now(), stages: [], outcome: 'preparing' };
    const timings = window.ftAddCasterInventoryTimings ??= [];
    timings.push(timing); if (timings.length > 32) timings.shift();
    perf?.mark(prefix);
    let stageSequence = 0;
    const time = async <T>(name: string, action: () => T | Promise<T>): Promise<T> => {
        const mark = `${prefix}:${name}:${++stageSequence}`, start = now();
        perf?.mark(mark);
        try { return await action(); }
        finally {
            timing.stages.push({ name, ms: now() - start });
            perf?.measure(mark, mark); perf?.clearMarks(mark);
        }
    };
    const finish = (outcome: string): void => {
        timing.outcome = outcome; timing.totalMs = now() - started;
        perf?.measure(`${prefix}:total`, prefix);
    };
    const warning = document.getElementById(warningId);
    const fingerprint = JSON.stringify([id, values.item_id, values.item_qty, values.enchantment_template_id]);
    const page = window as unknown as Page;
    let inventoryOwner: Row | null = null;
    const receiverFor = (): unknown => {
        // A container already carried by a held creature is the canonical row, even with no object-cache entry.
        const roots = new Set<Row>();
        for (const cache of [page.FateTide?.casterCache, page.FateTide?.objectCache])
            for (const host of cache?.values?.() ?? []) if (fromBaseRecord(host)) roots.add(host);
        for (const scene of page.FateTide?.encounterCache?.values?.() ?? [])
            if (fromBaseRecord(scene) && Array.isArray(scene.encounter_creatures_arr))
                for (const host of scene.encounter_creatures_arr) if (fromBaseRecord(host)) roots.add(host);
        for (const host of roots) {
            const carried = findCarriedInventoryEntry(host, id, { inventoryField: readInventoryField,
                entries: (row, key) => readInventoryRows(row, key === 'inventory' ? 'inventory' : 'inventory_in_container') });
            if (carried) { inventoryOwner = host; return carried; }
        }
        const cached = (): unknown => page.FateTide?.casterCache?.get(id) ?? page.FateTide?.objectCache?.get(id);
        if (!cached()) page.ftSeedCasterFromEncounterCache?.(id);
        return cached();
    };
    const receiverId = (host: Row): string => String(encounterCreatureField(fromBaseRecord(host.encounter_creatures) ? host.encounter_creatures : host, 'encounter_creature_id')
        || surfaceField(fromBaseRecord(host.player_characters) ? host.player_characters : null, 'item_id')
        || surfaceField(fromBaseRecord(host.items) ? host.items : null, 'item_id') || id).toUpperCase();
    const renderReceiverId = (): string => inventoryOwner ? receiverId(inventoryOwner) : id;
    const heldFor = (receiver: unknown): Row | null => {
        if (!fromBaseRecord(receiver)) return null;
        if (inventoryOwner && String(readInventoryField(receiver, 'inventory_id')).toUpperCase() === id) return heldFor(inventoryOwner);
        const own = fromBaseRecord(receiver._entity_base) ? receiver._entity_base : null;
        const encounterId = String(encounterCreatureField(fromBaseRecord(receiver.encounter_creatures) ? receiver.encounter_creatures : receiver, 'encounter_id') ?? '');
        const scene = page.FateTide?.encounterCache?.get(encounterId);
        if (!fromBaseRecord(scene) || scene._entity_refusal || !Array.isArray(scene.encounter_creatures_arr)
            || !scene.encounter_creatures_arr.includes(receiver) || !fromBaseRecord(scene._entity_base)) return own;
        const envelope = scene._entity_base;
        if (typeof envelope.base_rev !== 'number' || envelope.base_rev < (page.ftBaseHeardRevision?.(encounterId) ?? envelope.base_rev)
            || !fromBaseRecord(envelope.base) || !Array.isArray(envelope.base.encounter_creatures_arr)) return own;
        const member = envelope.base.encounter_creatures_arr.find(value => fromBaseRecord(value) && String(value.entity_id).toUpperCase() === receiverId(receiver));
        return fromBaseRecord(member) && typeof member.base_rev === 'number' && (!own || member.base_rev >= Number(own.base_rev ?? -1))
            ? { base_rev: member.base_rev, base: member } : own;
    };
    let queued = false, undo: (() => void) | undefined;
    let settleBirth: ReturnType<typeof holdInventoryBirth> | undefined;
    const warn = (message: string, unported = false): void => {
        const text = `Could not add inventory: ${message}`;
        if (warning) warning.textContent = text;
        if (unported) refuseUnported(message); else window.displayWarning?.(text);
    };
    try {
        if (!fromBaseUuid(id)) throw new Error('add-inventory:receiver-required');
        let prepared = pending.get(id);
        if (prepared?.fingerprint !== fingerprint) prepared = undefined;
        if (!prepared) {
            const itemId = String(values.item_id ?? '').trim().toUpperCase(), template = String(values.enchantment_template_id ?? '').trim().toUpperCase();
            if (!fromBaseUuid(itemId)) throw new Error('add-inventory:item-required');
            if (!await time('enchantment', () => window.ftAllowInventoryEnchantment(itemId, template))) throw new Error('add-inventory:enchantment-not-allowed');
            let receiver = receiverFor();
            const held = heldFor(receiver);
            const revision = held?.base_rev;
            const fresh = fromBaseRecord(receiver) && typeof revision === 'number' && Number.isSafeInteger(revision)
                && !held?.resync && !receiver._entity_refusal && revision >= (page.ftBaseHeardRevision?.(id) ?? revision);
            if (!fresh) {
                if (typeof fetchAndCacheCaster !== 'function') throw new Error('add-inventory:receiver-reader-unavailable');
                await time('receiver', () => fetchAndCacheCaster(id, { skipEncounterSeed: true, throwOnError: true }));
                receiver = receiverFor();
            }
            if (!fromBaseRecord(receiver)) throw new Error('add-inventory:receiver-unavailable');
            const envelope = heldFor(receiver);
            const base = fromBaseRecord(envelope?.base) ? envelope.base : null;
            const context = fromBaseRecord(base?.encounter_context) ? base.encounter_context : fromBaseRecord(receiver.encounter_context) ? receiver.encounter_context : null;
            const scene = page.FateTide?.encounterCache?.get(String(encounterCreatureField(fromBaseRecord(receiver.encounter_creatures) ? receiver.encounter_creatures : receiver, 'encounter_id') ?? ''));
            const campaignId = String(ftClockCampaignField(context?.campaign_arr, 'campaign_id') || surfaceField(fromBaseRecord(receiver.player_characters) ? receiver.player_characters : {}, 'attached_campaign_id')
                || (fromBaseRecord(scene) ? surfaceField(fromBaseRecord(scene.encounters) ? scene.encounters : scene, 'encounter_master_parent_id') : '') || '');
            const campaignReady = (async () => {
                let campaign = page.FateTide?.campaignCache?.get(campaignId) ?? context?.campaign_arr;
                if (!campaign && fromBaseUuid(campaignId)) campaign = await time('campaign', async () => {
                    const response = await fetch(BASE_URL + 'options/api/base_entity_inc.php?entity=campaign&entity_id=' + encodeURIComponent(campaignId));
                    if (!response.ok) throw new Error('add-inventory:campaign-inputs-unavailable');
                    const envelope: unknown = await response.json();
                    if (!fromBaseRecord(envelope) || !fromBaseRecord(envelope.base)) throw new Error('add-inventory:campaign-inputs-unavailable');
                    return envelope.base;
                });
                if (fromBaseRecord(campaign) && !ftClockCalendarFromArr(campaign).months.length) {
                    const calendarId = String(ftClockCampaignField(campaign, 'calendar_id') ?? '');
                    if (fromBaseUuid(calendarId)) {
                        const calendar = await time('calendar', () => calendarMonths(calendarId));
                        const computed = fromBaseRecord(campaign.computed) ? campaign.computed : {};
                        campaign = { ...campaign, computed: { ...computed, campaign: { ...fromBaseRecord(computed.campaign) ? computed.campaign : {}, calendar } } };
                    }
                }
                return campaign;
            })();
            const templates = new Set([...inventoryCreationSizeTemplateIds(), ...template ? [template] : []]);
            const walk = (host: Row, nested = false): void => { for (const row of readInventoryRows(host, nested || readInventoryField(host, 'inventory_id') ? 'inventory_in_container' : 'inventory')) {
                const id = String(readInventoryField(row, 'enchantment_template_id') ?? ''); if (fromBaseUuid(id)) templates.add(id); walk(row, true);
            } };
            walk(receiver);
            // No destination_id: the source SELECT's generator destination gate is unrelated to players.
            const ids = [...templates], chunks: Promise<SpawnSourcePackage>[] = [];
            for (let offset = 0; offset < ids.length; offset += 128) chunks.push(time('spawn-inputs', async () => {
                const response = await fetch(BASE_URL + 'options/api/spawn_inputs_inc.php', { method: 'POST', credentials: 'same-origin',
                    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: [itemId, SPOILING_STATUS_ID], enchantment_template_ids: ids.slice(offset, offset + 128) }) });
                const data: unknown = await response.json();
                if (!response.ok || !fromBaseRecord(data) || !fromBaseRecord(data.base) || !fromBaseRecord(data.metadata) || !fromBaseRecord(data.shared)) throw new Error('add-inventory:definitions-unavailable');
                return data as unknown as SpawnSourcePackage;
            }));
            const [campaign, packages] = await Promise.all([campaignReady, Promise.all(chunks)]);
            // Promise.all preserves request order, including overlapping catalog rows.
            const input = packages.reduce<SpawnSourcePackage | undefined>((input, next) => input ? { ...next, base: mergeInventoryMetadata(input.base, next.base) } : next, undefined);
            if (!input) throw new Error('add-inventory:definitions-unavailable');
            const roll = browserRollSource();
            const clock = campaign ? ftDeriveCampaignClock(ftClockBaseFromArr(campaign), Date.now()).cols : null;
            const command = await time('plan', () => planAddCasterInventory(input, { ...receiver, campaign_arr: campaign }, id, itemId, Number(values.item_qty), template,
                { uuid: () => crypto.randomUUID().toUpperCase(), die: sides => roll.d(sides), clock: () => clock }));
            prepared = { fingerprint, command, input }; pending.set(id, prepared);
        }
        const { command } = prepared;
        const birthIds = command.inserts.filter(entry => entry.kind === 'inventory')
            .map(entry => String(readInventoryField({ inventory: entry.row }, 'inventory_id')));
        let birthSaved = false;
        let birthParked = false;
        let deliveryWarned = false;
        let sourceForPaint: SpawnSourcePackage | undefined = prepared.input;
        let published = false;
        const holdBirth = (): ReturnType<typeof holdInventoryBirth> => holdInventoryBirth(birthIds, () => {
            birthSaved = true; pending.delete(id); running.delete(id);
            if (warning && timing.outcome !== 'saved') warning.textContent = command.summary;
            undo = undefined; sourceForPaint = undefined; finish('saved');
        });
        settleBirth = holdBirth();
        timing.operationId = command.operation_id;
        if (warning) warning.textContent = 'Adding inventory…';
        const paint = async (): Promise<void> => {
            if (undo) return;
            await time('paint', () => {
                if (sourceForPaint) undo = page.ftPaintCasterInventoryPlan?.(command, sourceForPaint);
                const applied = (undo as { applied_frame?: AppliedFrame } | undefined)?.applied_frame;
                if (!published && applied?.encounter_id && window.ftPushAppliedTruth) {
                    published = true;
                    void window.ftPushAppliedTruth({ type: 'janus_inventory_truth', command: { ...command,
                        encounter_id: applied.encounter_id, actor_id: applied.actor_id,
                        rows: [{ roll_type_1: 'add_inventory', roll_by: applied.actor_id, roll_to: id, rolls_full: command.summary }] }, applied_frame: applied });
                }
                page.ftRerenderPcSheet?.(renderReceiverId(), ['inventory']);
            });
            timing.paintedMs = now() - started;
        };
        const restore = async (message: string, terminal = false): Promise<void> => {
            if (birthSaved) return;
            undo?.(); undo = undefined;
            page.ftRerenderPcSheet?.(id);
            if (terminal || !deliveryWarned) { warn(message); deliveryWarned = true; }
            finish('refused');
            // Recovery is optional I/O and may be slow too. The targeted rollback
            // already finished: release dependent actions before awaiting recovery.
            settleBirth?.({ ok: false, error: 'inventory-birth:save-unconfirmed' }, true);
            const receiver = inventoryOwner ?? receiverFor();
            const encounterId = fromBaseRecord(receiver) ? String(encounterCreatureField(fromBaseRecord(receiver.encounter_creatures) ? receiver.encounter_creatures : receiver, 'encounter_id') ?? '') : '';
            try {
                if (encounterId && page.ftResyncEncounter) await page.ftResyncEncounter(encounterId);
                else if (typeof fetchAndCacheCaster === 'function') await fetchAndCacheCaster(id, { skipEncounterSeed: true, throwOnError: true });
                // The optimistic rows were removed before this server read. Only
                // exact returned birth identities prove a lost answer was saved.
                const recovered = receiverFor();
                if (fromBaseRecord(recovered) && birthIds.length && birthIds.every(birthId => !!findCarriedInventoryEntry(recovered, birthId, {
                    inventoryField: readInventoryField, entries: (row, key) => readInventoryRows(row, key === 'inventory' ? 'inventory' : 'inventory_in_container'),
                }))) settleBirth?.({ ok: true });
            } catch { /* The targeted rollback already removed unsaved rows. */ }
        };
        const send = async (): Promise<{ ok: boolean; status: number; error?: string }> => {
            if (birthSaved) return { ok: true, status: 200 };
            // A parked delivery ended the prior UI wait. Its same immutable save can
            // repaint on retry; register a fresh row wait BEFORE that row reappears.
            if (queued && birthParked) { settleBirth = holdBirth(); birthParked = false; }
            const abort = new AbortController();
            const timeout = setTimeout(() => abort.abort(), 20_000);
            try {
                if (queued) await paint();
                const { response, result } = await time('commit', async () => {
                    const response = await withAsyncDeadline(() => fetch(BASE_URL + 'options/game/actions/caster_inventory_truth_commit_inc.php', { method: 'POST', credentials: 'same-origin',
                        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(command), signal: abort.signal }), 20_000);
                    if (published) await window.ftSaveAppliedTruth?.(command.operation_id, async () => response);
                    timing.commitAnswerMs = now() - started;
                    const result = await withAsyncDeadline(() => response.json(), 20_000) as { ok?: boolean; error?: string; base_refresh?: BasePatchFrame };
                    return { response, result };
                });
                if (response.status === 409 && result.error === 'add-inventory:stale-stack') pending.delete(id);
                if (!response.ok || !result.ok) return { ok: false, status: response.status, error: result.error ?? `add-inventory:save-http:${response.status}` };
                pending.delete(id);
                if (warning) warning.textContent = command.summary;
                try {
                    if (!published && result.base_refresh) await time('base-refresh', () => window.ftApplyBasePatch?.(result.base_refresh!, true));
                    await time('rerender', () => page.ftRerenderPcSheet?.(renderReceiverId(), ['inventory']));
                } catch { if (warning) warning.textContent = `${command.summary} Reopen inventory to see the saved rows.`; }
                undo = undefined; sourceForPaint = undefined; finish('saved'); running.delete(id);
                settleBirth?.({ ok: true });
                return { ok: true, status: response.status };
            } catch (error) {
                if (queued) await restore('Delivery is unconfirmed. Do not add another copy while this save is pending.');
                if (birthSaved) return { ok: true, status: 200 };
                throw error;
            } finally { clearTimeout(timeout); }
        };
        if (page.ftPaintCasterInventoryPlan && (window.ftQueueActorSave || window.ftPushAppliedTruth)) {
            try { await paint(); queued = !!window.ftQueueActorSave; }
            catch (error) {
                timing.paintError = error instanceof Error ? error.message : String(error);
                try { undo?.(); } catch { /* A local rollback failure must not prevent saving. */ }
                undo = undefined;
            }
        }
        if (queued) {
            timing.outcome = 'queued';
            const receiver = inventoryOwner ?? receiverFor();
            const encounterId = fromBaseRecord(receiver) ? String(encounterCreatureField(fromBaseRecord(receiver.encounter_creatures) ? receiver.encounter_creatures : receiver, 'encounter_id') ?? '') : '';
            const save: AppliedSaveInput = { encounterId, actorIds: [id], command, send,
                parked: () => { birthParked = true; running.delete(id); settleBirth?.({ ok: false, error: 'inventory-birth:delivery-parked' }, true); },
                refused: async info => {
                    // Terminal entries are retained by the shared outbox for deduplication. A new click must plan a new operation;
                    // uncertain transport failures retry send() with the original operation id instead.
                    pending.delete(id);
                    try { await restore(info.error, true); } finally {
                        running.delete(id); settleBirth?.({ ok: false, error: info.error });
                    }
                } };
            // The websocket bundle owns the queue; this bundle imports only its type.
            window.ftQueueActorSave!(save);
        } else {
            const answer = await send();
            if (!answer.ok) throw new Error(answer.error);
        }
    } catch (error) {
        queued = false;
        try { undo?.(); if (undo) page.ftRerenderPcSheet?.(id); }
        catch { /* Rollback rendering must not hide the original failure. */ }
        warn(error instanceof Error ? error.message : 'add-inventory:failed', true); finish('failed');
        settleBirth?.({ ok: false, error: error instanceof Error ? error.message : 'add-inventory:failed' });
    } finally { if (!queued) running.delete(id); }
}
declare global { interface Window { ftAddCasterInventory?: typeof addCasterInventory; ftAddCasterInventoryTimings?: AddTiming[] } }
window.ftAddCasterInventory = addCasterInventory;
